import { useEffect, useRef } from 'react';

import type { Locale } from '@llura/constants';

import type { ArchetypeId } from '@llura/types';

import { useSession } from '../lib/auth-client';
import { api } from '../lib/api-client';
import { useCoachStore } from '../stores/coach.store';
import { useCovenantStore } from '../stores/covenant.store';
import { useRitualStore } from '../stores/ritual.store';
import { useProgressStore } from '../stores/progress.store';
import { useJournalStore } from '../stores/journal.store';
import { useChatStore } from '../stores/chat.store';
import { useIntroStore } from '../stores/intro.store';
import { useI18nStore } from '../stores/i18n.store';
import { usePersonalityStore } from '../stores/personality.store';
import { useNotificationPrefsStore } from '../stores/notification-prefs.store';
import { useBootSyncStore } from '../stores/boot-sync.store';
import { resetAllUserStores } from '../stores/reset-all';
import { snapshotForClaim } from '../lib/claim-snapshot';
import { isArchetypeId } from '../lib/archetypes';

// Defensive map for any pre-archetype payload still in the wild — the API
// already normalises, but a stale CDN response or out-of-date worker could
// hand us a legacy id.
const LEGACY_MODE_MAP: Record<string, ArchetypeId> = {
  structured: 'queen',
  edging: 'enchantress',
  exploratory: 'sensualist',
  'pleasure-taskmaster': 'queen',
  edged: 'enchantress',
  'sensory-quest': 'sensualist',
};

function mapMode(mode: string): ArchetypeId {
  if (isArchetypeId(mode)) return mode;
  return LEGACY_MODE_MAP[mode] ?? 'queen';
}

// Boot sync — server is the source of truth. On a new session (post-login,
// post-signup, or page reload while signed in), fetch /me + the latest journal
// page and overwrite local stores. On sign-out, reset every store so a
// second user on the same browser starts clean.
export function useBootSync() {
  const session = useSession();
  const prevUserId = useRef<string | null>(null);

  const hydrateCoach = useCoachStore((s) => s.hydrate);
  const hydrateCovenant = useCovenantStore((s) => s.hydrate);
  const hydrateRitual = useRitualStore((s) => s.hydrate);
  const hydrateProgress = useProgressStore((s) => s.hydrate);
  const hydrateJournal = useJournalStore((s) => s.hydrate);
  const hydrateIntro = useIntroStore((s) => s.hydrate);
  const hydrateI18n = useI18nStore((s) => s.hydrate);
  const hydratePersonality = usePersonalityStore((s) => s.hydrate);
  const hydratePrefs = useNotificationPrefsStore((s) => s.hydrate);
  const hydrateChat = useChatStore((s) => s.hydrate);
  const setBootStatus = useBootSyncStore((s) => s.setStatus);

  useEffect(() => {
    const userId = session.data?.user.id ?? null;

    // Sign-out: transition from "had a user" to "no user". Wipe local state
    // so the next user on this browser starts clean. The registry of stores
    // to wipe lives in `stores/reset-all.ts` — add new ones there.
    if (prevUserId.current && !userId) {
      resetAllUserStores();
    }
    prevUserId.current = userId;

    if (!userId) {
      // No session (once the session check resolves) → nothing to wait for.
      // Release the root-route gate so the offline/localStorage path routes
      // instantly. While the session check is still pending, leave the status
      // untouched so RootRedirect keeps waiting.
      if (!session.isPending) setBootStatus('settled');
      return;
    }

    // A session exists — gate the root route until /me has hydrated the stores.
    setBootStatus('syncing');

    let cancelled = false;
    // Claim the local pre-auth draft BEFORE hydrating. On a first login
    // (especially OAuth, where the session lands immediately) the server is
    // empty; without this, hydrate would overwrite the local coach/covenant
    // with nulls. Claim is idempotent — a no-op for returning users — so it's
    // safe to run on every new session. Email signup also calls it directly,
    // but running it here covers the OAuth redirect path uniformly.
    const claimFirst = async () => {
      const claim = snapshotForClaim({
        coach: useCoachStore.getState(),
        covenant: useCovenantStore.getState(),
        ritual: useRitualStore.getState(),
        intro: useIntroStore.getState(),
        i18n: useI18nStore.getState(),
        personality: usePersonalityStore.getState(),
      });
      if (Object.keys(claim).length === 0) return;
      try {
        await api.me.claim(claim);
      } catch (err) {
        console.warn('[boot-sync] claim failed', err);
      }
    };

    claimFirst()
      .then(() =>
        Promise.all([
          api.me.bundle(),
          api.journal.list(null, 50),
          api.chat.history().catch(() => ({ messages: [] })),
        ]),
      )
      .then(([me, journal, chat]) => {
        if (cancelled) return;
        hydrateCoach(me.coach);
        hydrateCovenant({
          boundaries: me.covenant.boundaries,
          safeWord: me.covenant.safeWord,
          hasAccepted: me.covenant.hasAccepted,
        });
        hydrateRitual(me.ritual);
        hydrateProgress({
          devotionLevel: me.progress.devotionLevel,
          sessionsCompleted: me.progress.sessionsCompleted,
          lastSessionAt: me.progress.lastSessionAt,
          unlockedModes: Array.from(
            new Set(me.progress.unlockedModes.map((m) => mapMode(m))),
          ),
          history: me.progress.recentSessions.map((r) => ({
            mode: mapMode(r.mode),
            level: r.level,
            points: r.points,
            at: r.at,
          })),
        });
        hydrateIntro(me.user.introCompleted);
        hydrateI18n(me.user.locale as Locale | null);
        hydratePrefs({
          reminderEnabled: me.user.reminderEnabled,
          reminderTime: me.user.reminderTime,
          timezone: me.user.timezone,
        });
        hydratePersonality({
          seen: me.user.personalityTestSeen,
          profile: me.personality,
        });
        hydrateJournal(
          journal.entries.map((e) => ({ id: e.id, body: e.body, mood: e.mood, at: e.at })),
        );
        // Chat: server wins, EXCEPT on a first login where the server is empty
        // but the user chatted offline — push the local conversation up instead
        // of letting an empty server history wipe it.
        const localChat = useChatStore.getState().messages;
        if (chat.messages.length === 0 && localChat.length > 0) {
          api.chat.save({ messages: localChat }).catch(() => undefined);
        } else {
          hydrateChat(
            chat.messages.map((m) => ({ id: m.id, from: m.from, text: m.text })),
          );
        }
      })
      .catch((err: unknown) => console.warn('[boot-sync] failed', err))
      .finally(() => {
        // Release the root gate whether the sync succeeded or failed — a failed
        // fetch must not strand the user on the loading screen. On failure they
        // fall through to whatever localStorage held, which is acceptable.
        if (!cancelled) setBootStatus('settled');
      });

    return () => {
      cancelled = true;
    };
  }, [
    session.data?.user.id,
    session.isPending,
    setBootStatus,
    hydrateCoach,
    hydrateCovenant,
    hydrateRitual,
    hydrateProgress,
    hydrateJournal,
    hydrateIntro,
    hydrateI18n,
    hydratePersonality,
    hydratePrefs,
    hydrateChat,
  ]);
}
